import { toast } from "sonner";
import api from "@/lib/api";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

let scriptPromise = null;
function loadPayPalScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    if (window.paypal) return resolve(true);
    if (!PAYPAL_CLIENT_ID) return resolve(false);
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&currency=USD&intent=capture&disable-funding=credit,card`;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Render PayPal Smart Buttons into a container and hook up create/capture.
 * @param {HTMLElement} container - target div
 * @param {string} certId
 * @param {() => void} onSuccess - called after successful capture
 * @param {string} [testCode] - optional PayPal test coupon (e.g. TESTPAY10 → $3 charge)
 */
export async function renderPayPalButtons(container, certId, onSuccess, testCode) {
  const ok = await loadPayPalScript();
  if (!ok || !window.paypal) {
    toast.error("Could not load PayPal. Check your connection or configuration.");
    return () => {};
  }

  // Clear any prior buttons before re-rendering (removeChild is XSS-safe)
  while (container.firstChild) container.removeChild(container.firstChild);

  const buttons = window.paypal.Buttons({
    style: {
      layout: "vertical",
      color: "gold",
      shape: "pill",
      label: "paypal",
      height: 44,
    },
    createOrder: async () => {
      try {
        const r = await api.post(`/payment/order/${certId}`, testCode ? { test_code: testCode } : {});
        if (r.data.already_paid) {
          toast.success("Certificate already unlocked.");
          onSuccess?.();
          throw new Error("already_paid");
        }
        if (r.data.test_mode) {
          toast(`Test order: PayPal will charge $${r.data.amount}`, { icon: "🧪" });
        }
        return r.data.order_id;
      } catch (err) {
        if (err.message !== "already_paid") {
          toast.error(err.response?.data?.detail || "Could not create PayPal order");
        }
        throw err;
      }
    },
    onApprove: async (data) => {
      try {
        await api.post(`/payment/capture/${data.orderID}`);
        toast.success("Payment successful — certificate unlocked!");
        onSuccess?.();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Payment capture failed");
      }
    },
    onCancel: () => {
      toast("Payment cancelled — you can retry anytime.", { icon: "ℹ️" });
    },
    onError: (err) => {
      console.error("PayPal error", err);
      toast.error("PayPal encountered an error. Please try again.");
    },
  });

  if (!buttons.isEligible()) {
    while (container.firstChild) container.removeChild(container.firstChild);
    const msg = document.createElement("div");
    msg.className = "text-xs text-zinc-500";
    msg.textContent = "PayPal is not available in your region.";
    container.appendChild(msg);
    return () => {};
  }

  buttons.render(container).catch((err) => {
    console.error("PayPal render failed", err);
  });

  return () => {
    try { buttons.close(); } catch (err) { console.error("PayPal close failed", err); }
  };
}
